import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { callRespondReviewLongevity } from '@/lib/callables';
import { wearLifeFor } from '@/lib/wearLife';
import { REVIEW_TEXT_MAX_CHARS, type ServiceCategory } from '@makhe/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  reviewId: string;
  category?: ServiceCategory;
}

type Holding = 'perfect' | 'loosening' | 'redo';

const HOLDING_OPTIONS: { value: Holding; label: string }[] = [
  { value: 'perfect', label: 'Still perfect' },
  { value: 'loosening', label: 'Starting to loosen' },
  { value: 'redo', label: 'Time for a redo' },
];

// Stage-2 of the two-stage review: how long did the result actually last? Opened
// from the follow-up notification (~3 weeks after the service) and written onto
// the existing review. "Still going" is recorded as a floor (stillGoing: true).
export function ReviewLongevitySheet({ open, onClose, reviewId, category }: Props) {
  const meta = category ? wearLifeFor(category) : null;
  const noun = meta?.noun ?? 'result';
  const options = meta?.options ?? [1, 2, 3, 4, 6, 8];

  const [holding, setHolding] = useState<Holding | null>(null);
  const [weeks, setWeeks] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const stillGoing = holding != null && holding !== 'redo';

  const reset = () => {
    setHolding(null);
    setWeeks(null);
    setNote('');
  };

  const answer = async () => {
    if (holding == null) return toast.error('Let us know how it’s holding up.');
    if (weeks == null) return toast.error('Pick roughly how many weeks it lasted.');
    setBusy(true);
    try {
      await callRespondReviewLongevity({
        reviewId,
        action: 'answer',
        wearWeeks: weeks,
        stillGoing,
        note: note.trim() || undefined,
      });
      toast.success('Thanks — this is exactly what other students want to know.');
      reset();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const later = async () => {
    setBusy(true);
    try {
      await callRespondReviewLongevity({ reviewId, action: 'snooze' });
      toast.success('No worries — we’ll nudge you again later today.');
      onClose();
    } catch {
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await callRespondReviewLongevity({ reviewId, action: 'dismiss' });
      onClose();
    } catch {
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Still going strong?">
      <div className="space-y-4">
        <div>
          <p className="text-sm text-makhe-ink/70">How’s {noun} holding up now?</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {HOLDING_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setHolding((cur) => (cur === o.value ? null : o.value))}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  holding === o.value
                    ? 'border-makhe-ochre bg-makhe-ochre/15 text-makhe-ochre'
                    : 'border-makhe-line text-makhe-ink/70 hover:border-makhe-ink'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-sm text-makhe-ink/70">
            About how many weeks did it last{stillGoing ? ' so far' : ''}?
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {options.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWeeks((cur) => (cur === w ? null : w))}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  weeks === w
                    ? 'border-makhe-ochre bg-makhe-ochre/15 text-makhe-ochre'
                    : 'border-makhe-line text-makhe-ink/70 hover:border-makhe-ink'
                }`}
              >
                {w} {w === 1 ? 'wk' : 'wks'}
              </button>
            ))}
          </div>
        </div>

        <Textarea
          label="Anything to add for future buyers? (optional)"
          rows={2}
          value={note}
          maxLength={REVIEW_TEXT_MAX_CHARS}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. edges held up great, still neat at week 3"
        />

        <Button onClick={answer} loading={busy} className="w-full">
          Add to my review
        </Button>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={later}
            disabled={busy}
            className="text-sm text-makhe-primary underline disabled:opacity-50"
          >
            Remind me later today
          </button>
          <button
            type="button"
            onClick={dismiss}
            disabled={busy}
            className="text-xs text-makhe-muted hover:text-makhe-ink disabled:opacity-50"
          >
            Don’t ask again
          </button>
        </div>
      </div>
    </Modal>
  );
}
